import React, { type JSX } from 'react';
import {
    Box,
    Divider,
    Grid,
    Paper,
    Typography,
    Skeleton,
    Button,
    Stack,
    TextField,
    Container,
    FormHelperText,
} from '@mui/material';
import List from '@mui/material/List';
import { useParams } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import { useAppDispatch, useTypedSelector } from '@fiji/common/src/app/store';
import {
    useGetTimelineDetailsByIdQuery,
    useUpdateAcknowledgeMutation,
} from '@fiji/common/src/features/Timeline/timelineApi';
import SimpleCardSkeleton from '../SimpleCardSkeleton';
import { useTransformData } from 'hooks/useTransformData';
import {
    setMessageContent,
    setPageHeaderData,
    setTimelineStatusColor,
} from '@fiji/common/src/features/common/commonSlice';
import NotificationsOff from '@mui/icons-material/NotificationsOff';
import { CustomModal, ModalActions } from 'components/CustomModal';
import { useConfirm } from '@fiji/common/src/hooks';
import { selectedOrg } from '@fiji/common/src/features/orgManagement/orgSlice';
import { EventDetails } from './EventDetails';
import { DeviceDetails } from './DeviceDetails';
import moment from 'moment';
import { AlarmOccurred } from './AlarmOccurred';
import { STATUS_COLORS } from 'pages/WidgetManagement/common';
import { useTranslation } from 'react-i18next';
import { CustomTransComponent } from 'components';
import { selectCurrentPermission } from '@fiji/common/src/features/profile/profileSlice';
import { useNewCustomTranslations, useRBAC } from 'hooks';

export const TimeLineDetails = (): JSX.Element => {
    const theme: any = useTheme();
    const { t } = useTranslation();
    const { timeLineId } = useParams();
    const [acknowledgementComment, setAcknowledgementComment] = React.useState('');
    const [isRetry, setIsRetry] = React.useState(false);
    const [discard, setDiscard] = React.useState(false);
    const currentOrg = useTypedSelector(selectedOrg);
    const permission = useTypedSelector(selectCurrentPermission);
    const { hasPermission } = useRBAC(permission);
    const canAcknowledge = hasPermission('acknowledgement_acknowledge-alarms');
    useNewCustomTranslations('TIMELINE');
    useNewCustomTranslations('ACCEPT_EULA');
    useNewCustomTranslations('DASHBOARD_MANAGEMENT');
    const {
        currentData: data,
        isLoading,
        isFetching,
    }: any = useGetTimelineDetailsByIdQuery(timeLineId, { refetchOnMountOrArgChange: true });

    const handleKeyDown = (event: any): any => {
        if (event.key === 'Enter') {
            event.preventDefault();
        }
    };

    const dispatch = useAppDispatch();
    const { convertDateTime } = useTransformData();
    const [updateAcknowledge, { isLoading: isUpdating }] = useUpdateAcknowledgeMutation();

    const calculateTimeDiffs = (times: any): any => {
        const timesUpdated = JSON.parse(JSON.stringify(times));

        Object.keys(times ?? {}).forEach((key) => {
            const diffMs = Date.now() - (times[key] ?? 0);
            const diffSec = Math.floor(diffMs / 1000);
            const diffMin = Math.floor(diffSec / 60);

            if (diffMin < 1) {
                timesUpdated[key] = { type: 'sec', value: diffSec };
            } else {
                timesUpdated[key] = { type: 'min', value: diffMin };
            }
        });

        return timesUpdated;
    };

    const [timeDiffs, setTimeDiffs] = React.useState(() =>
        calculateTimeDiffs({
            alarmUpdated: data?.data?.alarmUpdated,
            alarmOccurred: data?.data?.alarmOccurred,
            ...(data?.data?.alarmCleared && { alarmCleared: data?.data?.alarmCleared }),
        })
    );

    React.useEffect(() => {
        const interval = setInterval(() => {
            const updatedDiffs = calculateTimeDiffs({
                alarmUpdated: data?.data?.alarmUpdated,
                alarmOccurred: data?.data?.alarmOccurred,
                ...(data?.data?.alarmCleared && { alarmCleared: data?.data?.alarmCleared }),
            });
            // Stop interval when all values reach 60 min
            if (Object.values(updatedDiffs).every((min: any) => min.type === 'min' && min >= 60)) {
                clearInterval(interval);
            }
            setTimeDiffs(updatedDiffs);
        }, 10000); // Update every minute

        return () => clearInterval(interval); // Cleanup on unmount
    }, [data]);

    const submitHandler = async (): Promise<void> => {
        const { error }: any = await updateAcknowledge({
            id: timeLineId,
            groupId: currentOrg?.id,
            deviceId: data?.data?.deviceId,
            acknowledgementComment: acknowledgementComment?.trim(),
        });
        if (error) {
            setIsRetry(true);
            onCancel();
        } else {
            dispatch(
                setMessageContent({
                    isOpen: true,
                    message: t('TIMELINE:ALARM_ACKNOWLEDGED_SUCCESSFULLY'),
                })
            );
            onCancel();
            setAcknowledgementComment('');
        }
    };
    const { isVisible, onClick, onCancel, onConfirm } = useConfirm(submitHandler);

    React.useEffect(() => {
        if (data) {
            dispatch(setPageHeaderData(data?.data?.deviceTimeline));
            const updatedDiffs = calculateTimeDiffs({
                alarmUpdated: data?.data?.alarmUpdated,
                alarmOccurred: data?.data?.alarmOccurred,
                ...(data?.data?.alarmCleared && { alarmCleared: data?.data?.alarmCleared }),
            });
            setTimeDiffs(updatedDiffs);

            dispatch(
                setTimelineStatusColor(
                    STATUS_COLORS[data?.data?.status === 'Active' ? data?.data?.severity : 'Informational'] ??
                        STATUS_COLORS?.Informational
                )
            );
            setAcknowledgementComment(data?.data?.acknowledgementComment);
        }
        return (): void => {
            dispatch(setTimelineStatusColor(''));
        };
    }, [data]);

    const AckActions: ModalActions[] = [
        {
            testId: 'cancel',
            key: 'cancel',
            label: t('COMMON:CANCEL'),
            // close: true,
            variant: 'outlined',
            handleClick: (): void => {
                onCancel();
                if (acknowledgementComment?.length) setDiscard(true);
            },
        },
        {
            testId: 'acknowledge',
            key: 'save',
            label: t('COMMON:ACKNOWLEDGE_NAME'),
            // close: true,
            disabled: !acknowledgementComment?.trim().length,
            variant: 'contained',
            handleClick: onConfirm,
        },
    ];

    const DiscardActions: ModalActions[] = [
        {
            key: 'back',
            label: t('ACCEPT_EULA:BACK_BTN'),
            // close: true,
            variant: 'outlined',
            handleClick: (): void => {
                onClick();
                setDiscard(false);
            },
        },
        {
            key: 'discard',
            label: t('COMMON:DISCARD'),
            // close: true,
            variant: 'contained',
            className: 'error-btn',
            color: 'error',
            handleClick: (): void => {
                setAcknowledgementComment('');
                setDiscard(false);
            },
        },
    ];

    const renderAcknowledgementHeading = (): JSX.Element => {
        if (isLoading || isFetching) {
            return (
                <div style={{ padding: '6px' }}>
                    <Skeleton animation="wave">
                        <Typography variant="body1">
                            <CustomTransComponent translationKey={'DASHBOARD_MANAGEMENT:ACKNOWLEDGEMENT_DETAILS'} />
                        </Typography>
                    </Skeleton>
                </div>
            );
        }
        return (
            <Typography variant="body1" color="primary" sx={{ padding: '16px', fontSize: '14px', fontWeight: '600' }}>
                <CustomTransComponent translationKey={'DASHBOARD_MANAGEMENT:ACKNOWLEDGEMENT_DETAILS'} />
            </Typography>
        );
    };

    const renderAcknowledgementSubheading = (): JSX.Element => {
        if (isLoading || isFetching) {
            return (
                <div style={{ padding: '6px' }}>
                    <Skeleton animation="wave">
                        <Typography variant="body1">
                            <CustomTransComponent
                                translationKey={'DASHBOARD_MANAGEMENT:DESCRIPTION_DEVICE_CAN_BE_ANYTHING'}
                            />
                        </Typography>
                    </Skeleton>
                </div>
            );
        }
        return (
            <Stack p={2} spacing={2}>
                <Typography variant="body1" className="black-500">
                    {data?.data?.acknowledgementComment ?? '-'}
                </Typography>
                <Typography variant="caption" className="color-content" fontSize={'14px'} fontWeight={'700'}>
                    {`${t('DASHBOARD_MANAGEMENT:ACKNOWLEDGED_BY')} ${data?.data?.acknowledgedBy} ${moment(
                        data?.data?.alarmUpdated ?? null
                    ).format('DD/MM/YYYY')} at ${moment(data?.data?.alarmUpdated ?? null).format('h:mm:a')}`}
                </Typography>
            </Stack>
        );
    };

    const renderDescriptionTitle = (): JSX.Element => {
        if (isLoading || isFetching) {
            return (
                <div style={{ padding: '6px' }}>
                    <Typography variant="body1">
                        <CustomTransComponent translationKey={'COMMON:DESCRIPTION'} />
                    </Typography>
                </div>
            );
        }
        return (
            <Stack p={2}>
                <Typography
                    variant="body1"
                    fontSize={'14px'}
                    fontWeight={'600'}
                    sx={{ color: theme?.palette?.primary?.main }}
                >
                    <CustomTransComponent translationKey={'COMMON:DESCRIPTION'} />
                </Typography>
            </Stack>
        );
    };

    const renderDescription = (): JSX.Element => {
        if (isLoading || isFetching) {
            return (
                <div style={{ padding: '6px' }}>
                    <Skeleton animation="wave">
                        <Typography variant="body1">
                            <CustomTransComponent
                                translationKey={'DASHBOARD_MANAGEMENT:DESCRIPTION_DEVICE_CAN_BE_ANYTHING'}
                            />
                        </Typography>
                    </Skeleton>
                </div>
            );
        }
        return (
            <Stack p={2}>
                <Typography variant="body1" fontSize={'14px'} className="black-500">
                    {data?.data?.description ?? '-'}
                </Typography>
            </Stack>
        );
    };

    const shouldShowAcknowledgeButton =
        Boolean(data?.data) && canAcknowledge && !data?.data?.isAcknowledged && data?.data?.severity === 'Alarm';

    return (
        <Container maxWidth="xl" className="padding-y-24">
            <Grid container spacing={2}>
                <Grid size={12}>
                    {data?.data?.isAcknowledged ? (
                        <Paper>
                            {renderAcknowledgementHeading()}
                            <Divider />
                            {renderAcknowledgementSubheading()}
                        </Paper>
                    ) : (
                        shouldShowAcknowledgeButton && (
                            <Button
                                id="acknowledge"
                                sx={{
                                    backgroundColor: theme?.palette?.primary?.main,
                                    '&:hover': {
                                        backgroundColor: theme?.palette?.primary?.main,
                                    },
                                }}
                                variant="contained"
                                onClick={onClick}
                                startIcon={<NotificationsOff />}
                            >
                                <CustomTransComponent translationKey={'COMMON:ACKNOWLEDGE_NAME'} />
                            </Button>
                        )
                    )}
                </Grid>
                <Grid size={12}>
                    <Paper>
                        {renderDescriptionTitle()}
                        <Divider />
                        {renderDescription()}
                    </Paper>
                </Grid>
                <Grid size={6}>
                    <Paper sx={{ marginBottom: '16px' }}>
                        <EventDetails
                            isLoading={isFetching || isLoading}
                            data={data}
                            convertDateTime={convertDateTime}
                            alarmUpdatedDiff={timeDiffs?.['alarmUpdated'] ?? 1}
                        />
                    </Paper>
                    {Boolean(Object.keys(data?.data?.occurredTrends ?? {})?.length) && (
                        <Paper>
                            <AlarmOccurred
                                isLoading={isFetching || isLoading}
                                data={data}
                                convertDateTime={convertDateTime}
                                alarmOccurredDiff={timeDiffs?.['alarmOccurred'] ?? 1}
                            />
                        </Paper>
                    )}
                </Grid>

                <Grid size={6}>
                    <Paper sx={{ marginBottom: '16px' }}>
                        <List disablePadding>
                            {isLoading || isFetching ? (
                                <SimpleCardSkeleton count={3} />
                            ) : (
                                <>
                                    <Stack p={2} className="border-bottom-1">
                                        <Typography
                                            sx={{ fontSize: '14px', color: theme?.palette?.primary?.main }}
                                            fontWeight={'600'}
                                        >
                                            <CustomTransComponent translationKey={'DASHBOARD_MANAGEMENT:HISTORY'} />
                                        </Typography>
                                    </Stack>
                                    <Stack
                                        direction={'row'}
                                        className="border-bottom-1"
                                        p={2}
                                        alignItems={'center'}
                                        justifyContent={'space-between'}
                                    >
                                        <Typography className="text-secondary" fontSize={'15px'}>
                                            <CustomTransComponent translationKey={'COMMON:LAST_UPDATE_RECEIVED'} />
                                        </Typography>
                                        <Stack direction="row" alignItems="center">
                                            {data?.data?.alarmUpdated ? (
                                                <>
                                                    <Typography marginRight={2}>
                                                        {timeDiffs['alarmUpdated']?.type === 'sec'
                                                            ? `${timeDiffs['alarmUpdated']?.value} seconds ago`
                                                            : timeDiffs['alarmUpdated']?.type === 'min' &&
                                                              timeDiffs['alarmUpdated']?.value < 60
                                                            ? `${timeDiffs['alarmUpdated']?.value} minutes ago`
                                                            : ''}
                                                    </Typography>
                                                    <Typography
                                                        fontWeight="400"
                                                        fontSize="15px"
                                                        className="text-secondary"
                                                    >
                                                        {convertDateTime({
                                                            timestamp: parseInt(data.data.alarmUpdated),
                                                            customFormat: 'h:mm:ss',
                                                        })}
                                                    </Typography>
                                                    <Typography
                                                        fontWeight="600"
                                                        fontSize="15px"
                                                        className="text-secondary"
                                                        sx={{ marginLeft: '4px', marginRight: '4px' }}
                                                    >
                                                        {convertDateTime({
                                                            timestamp: parseInt(data.data.alarmUpdated),
                                                            customFormat: 'A',
                                                        })}
                                                    </Typography>
                                                    <Typography
                                                        fontWeight="400"
                                                        fontSize="15px"
                                                        className="text-secondary"
                                                    >
                                                        {convertDateTime({
                                                            timestamp: parseInt(data.data.alarmUpdated),
                                                        })}
                                                    </Typography>
                                                </>
                                            ) : (
                                                <Typography fontWeight="600" fontSize="15px" className="text-secondary">
                                                    --
                                                </Typography>
                                            )}
                                        </Stack>
                                    </Stack>

                                    {Boolean(data?.data?.alarmCleared) && (
                                        <>
                                            <Stack
                                                direction={'row'}
                                                className="border-bottom-1"
                                                p={2}
                                                alignItems={'center'}
                                                justifyContent={'space-between'}
                                            >
                                                <Typography className="text-secondary" fontSize={'15px'}>
                                                    <CustomTransComponent
                                                        translationKey={'COMMON:TIME_EVENT_CLEARED'}
                                                    />
                                                </Typography>
                                                <Stack direction={'row'} alignItems={'center'}>
                                                    <Typography marginRight={2}>
                                                        {timeDiffs['alarmCleared'] < 60
                                                            ? `${timeDiffs['alarmCleared']} minutes ago`
                                                            : ''}
                                                    </Typography>
                                                    <Typography
                                                        fontWeight={'400'}
                                                        fontSize={'15px'}
                                                        className="text-secondary"
                                                    >
                                                        {convertDateTime({
                                                            timestamp: parseInt(data?.data?.alarmCleared),
                                                            customFormat: 'h:mm:ss',
                                                        }) || '-'}
                                                    </Typography>
                                                    <Typography
                                                        fontWeight="600"
                                                        fontSize="15px"
                                                        className="text-secondary"
                                                        sx={{ marginLeft: '4px', marginRight: '4px' }}
                                                    >
                                                        {convertDateTime({
                                                            timestamp: parseInt(data.data.alarmCleared),
                                                            customFormat: 'A',
                                                        })}
                                                    </Typography>
                                                    <Typography
                                                        fontWeight={'400'}
                                                        fontSize={'15px'}
                                                        className="text-secondary"
                                                    >
                                                        {convertDateTime({
                                                            timestamp: parseInt(data?.data?.alarmCleared),
                                                        }) || '-'}
                                                    </Typography>
                                                </Stack>
                                            </Stack>
                                            <Divider />
                                        </>
                                    )}
                                    <Stack
                                        direction={'row'}
                                        className="border-bottom-1"
                                        p={2}
                                        alignItems={'center'}
                                        justifyContent={'space-between'}
                                    >
                                        <Typography className="text-secondary" fontSize={'15px'}>
                                            <CustomTransComponent translationKey={'AUDIT_LOGS:TIME_EVENT'} />
                                        </Typography>
                                        <Stack direction="row" alignItems="center">
                                            {data?.data?.alarmOccurred ? (
                                                <>
                                                    <Typography marginRight={2}>
                                                        {timeDiffs['alarmOccurred'] < 60
                                                            ? `${timeDiffs['alarmOccurred']} minutes ago`
                                                            : ''}
                                                    </Typography>
                                                    <Typography
                                                        fontWeight="400"
                                                        fontSize="15px"
                                                        className="text-secondary"
                                                    >
                                                        {convertDateTime({
                                                            timestamp: parseInt(data.data.alarmOccurred),
                                                            customFormat: 'h:mm:ss',
                                                        })}
                                                    </Typography>
                                                    <Typography
                                                        fontWeight="600"
                                                        fontSize="15px"
                                                        className="text-secondary"
                                                        sx={{ marginLeft: '4px', marginRight: '4px' }}
                                                    >
                                                        {convertDateTime({
                                                            timestamp: parseInt(data.data.alarmOccurred),
                                                            customFormat: 'A',
                                                        })}
                                                    </Typography>
                                                    <Typography
                                                        fontWeight="400"
                                                        fontSize="15px"
                                                        className="text-secondary"
                                                    >
                                                        {convertDateTime({
                                                            timestamp: parseInt(data.data.alarmOccurred),
                                                        })}
                                                    </Typography>
                                                </>
                                            ) : (
                                                <Typography fontWeight="600" fontSize="15px" className="text-secondary">
                                                    -
                                                </Typography>
                                            )}
                                        </Stack>
                                    </Stack>
                                </>
                            )}
                        </List>
                    </Paper>

                    <Paper>
                        <DeviceDetails isLoading={isFetching || isLoading} data={data} />
                    </Paper>
                </Grid>
            </Grid>
            <CustomModal
                key={'Acknowledges'}
                isOpen={isVisible}
                isLoading={isLoading || isUpdating}
                header={<CustomTransComponent translationKey={'DASHBOARD_MANAGEMENT:ACKNOWLEDGE_ALARM'} />}
                actions={AckActions}
                customHeader={
                    <Stack>
                        <Typography variant="h6" fontSize={'20px'} fontWeight={'600'}>
                            <CustomTransComponent translationKey={'DASHBOARD_MANAGEMENT:ACKNOWLEDGE_ALARM'} />
                        </Typography>
                        <Stack mt={2}>
                            <Box>
                                <Typography variant="body1">
                                    <CustomTransComponent
                                        translationKey={'DASHBOARD_MANAGEMENT:USE_THIS_FIELD_TO_GIVE_CONTEXT'}
                                    />
                                </Typography>
                            </Box>
                            <Box mt={1} mb={4}>
                                <Typography variant="body1">
                                    <CustomTransComponent
                                        translationKey={'DASHBOARD_MANAGEMENT:SEE_ALL_NOTES_LEFT_BY_EVERY_OPERATOR'}
                                    />
                                </Typography>
                            </Box>
                        </Stack>
                    </Stack>
                }
                type="small"
            >
                <Stack>
                    <TextField
                        id="notes"
                        variant="filled"
                        label={<CustomTransComponent translationKey={'COMMON:NOTES'} />}
                        value={acknowledgementComment}
                        multiline
                        onKeyDown={handleKeyDown}
                        maxRows={15}
                        onChange={(e: any): void => {
                            if (e.target.value.length > 256) {
                                return;
                            }
                            setAcknowledgementComment(e?.target?.value ?? '');
                        }}
                        className="max-height-500 overflow-y-scroll"
                    />
                    <Stack flexDirection={'row'} alignItems={'center'} justifyContent={'end'} px={2}>
                        <FormHelperText>{acknowledgementComment?.length ?? 0}/256</FormHelperText>
                    </Stack>
                </Stack>
            </CustomModal>
            <CustomModal
                key={'retry'}
                isOpen={isRetry}
                isLoading={isLoading}
                actions={[
                    {
                        key: 'save',
                        label: t('bluiRegistration:ACTIONS.OKAY'),
                        // close: true,
                        variant: 'contained',
                        handleClick: (): void => setIsRetry(false),
                    },
                ]}
                header={<CustomTransComponent translationKey={'COMMON:PLEASE_RETRY'} />}
                noDividers
                actionsDivider
                type="primary"
            >
                <Stack display="flex" flexDirection="column">
                    <Typography variant="body1">
                        <CustomTransComponent translationKey={'DEVICE_TIMELINE:ACKNOWLEDGEMENT_FAILED'} />{' '}
                        {convertDateTime({ timestamp: Date.now() })},{' '}
                        {convertDateTime({ timestamp: Date.now(), customFormat: 'h:mm:ss' })}{' '}
                        {convertDateTime({ timestamp: Date.now(), customFormat: 'A' })}. If this problem continues,
                        please call our support team at <a href="tel:877-386-2273">877-386-2273</a> or email{' '}
                        <a href="mailto:BLsupport@eaton.com">BLsupport@eaton.com</a>.
                    </Typography>
                </Stack>
            </CustomModal>
            <CustomModal
                key={'retry'}
                isOpen={discard}
                isLoading={isLoading}
                actions={DiscardActions}
                header="Discard Changes?"
                noDividers
                actionsDivider
                type="primary"
            >
                <Stack display="flex" flexDirection="column">
                    <Typography variant="body1">
                        <CustomTransComponent translationKey={'COMMON:ACTION_UNDONE_WARNING'} />
                    </Typography>
                </Stack>
            </CustomModal>
        </Container>
    );
};
